import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { site } from "../data/content";

const links = [
  { to: "/work", label: "Work", id: "nav-work-link" },
  { to: "/about", label: "About", id: "nav-about-link" },
  { to: "/recognition", label: "Recognition", id: "nav-recognition-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 bg-paper transition-[border-color,padding] duration-300 ${
          scrolled ? "border-b border-line" : "border-b border-transparent"
        }`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-6 transition-[padding] duration-300 ${
            scrolled ? "py-4" : "py-6"
          }`}
        >
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-display text-base font-bold tracking-[0.12em] transition-colors duration-300 hover:text-blue"
          >
            {site.name}
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:text-blue ${
                    isActive ? "text-blue" : "text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={site.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-resume-cta"
              className="hidden items-center gap-1 border border-ink px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-paper md:flex"
            >
              Resume <ArrowUpRight size={13} />
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="text-ink transition-colors duration-300 hover:text-blue md:hidden"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-paper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between px-6 py-6">
              <span className="font-display text-base font-bold tracking-[0.12em]">
                {site.name}
              </span>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                className="text-ink transition-colors duration-300 hover:text-blue"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6">
              {links.map((l, i) => (
                <span key={l.to} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "110%" }}
                    transition={{ duration: 0.6, delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-${l.id}`}
                      className={({ isActive }) =>
                        `font-display text-5xl font-bold tracking-tight transition-colors duration-300 hover:text-blue ${
                          isActive ? "text-blue" : "text-ink"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.span>
                </span>
              ))}
            </nav>
            <div className="border-t border-line px-6 py-6">
              <a
                href={site.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-resume-cta"
                className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke"
              >
                Resume <ArrowUpRight size={13} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
